// A cloud zone's pure rules for the control plane's pages (doc/20261018-cloud-zone-browser.md §3.2):
// no React and no antd here, so scripts/frontend-cloud.test.mjs runs them under node:test.
import type { CloudFacts, CloudRegion, Zone } from './api';

/**
 * The region the form starts on: the control plane's suggestion when it is offered (part 2 §4 I3: the China edge, then
 * Cloudflare's country); else the one nearer the deployer by the browser's time zone, Asia and Oceania choosing
 * Singapore when offered (card 46). The user can still change it.
 */
export function defaultRegion(regions: readonly CloudRegion[], timeZone: string, suggested?: string | null): string | undefined {
  const offered = suggested == null ? undefined : regions.find(region => region.id === suggested);
  if (offered !== undefined) return offered.id;
  const nearer = /^(Asia|Australia|Pacific|Indian)\//u.test(timeZone) ? regions.find(region => region.id === 'ap-southeast-1') : undefined;
  return (nearer ?? regions[0])?.id;
}

/** The control plane's TENANT_NAME (control/src/cloud.ts): checked as the user types, and again by the server. */
export const TENANT_NAME = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/u;

/**
 * The wait page's `return` (§3.2.4): a path only, starting with a single `/`, with no `\` and no control
 * characters; anything else is dropped. The host is always the state answer's `webAddress`.
 */
export function safeReturnPath(raw: string | null): string {
  if (raw === null || !raw.startsWith('/') || raw.startsWith('//') || raw.includes('\\')) return '';
  // eslint-disable-next-line no-control-regex
  return /[\u0000-\u001f\u007f]/u.test(raw) ? '' : raw;
}

/** A zone that is cloud and has its facts (a Worker that predates them sends none). */
export function cloudOf(zone: Zone): CloudFacts | null {
  return zone.topology === 'cloud' && zone.cloud !== undefined ? zone.cloud : null;
}

export type CloudStage = 'starting' | 'connected' | 'failed' | 'stopped';

/**
 * The China reachability check's terminal outcomes (part 2 §4 I3): the claim was released and the zone left asleep.
 * Its data is safe, and a wake checks again.
 */
export const CHINA_POWER_ERRORS: readonly string[] = ['china_unreachable', 'china_check_failed'];

/** Asleep after a wake that did not finish, the China check's included: offered a wake again. */
export function wakeDidNotFinish(cloud: CloudFacts | null): boolean {
  return cloud !== null && cloud.power === 'asleep' && (cloud.powerError === 'wake_not_ready' || CHINA_POWER_ERRORS.includes(cloud.powerError ?? ''));
}

/**
 * Step 3's stage from the fields the control plane really has (§3.2.2): failed when the onboarding failed;
 * connected once the daemon has checked in on an active tenant and it is awake; starting before that.
 */
export function cloudStage(zone: Zone): CloudStage {
  const cloud = cloudOf(zone);
  if (cloud?.lifecycle === 'failed') return 'failed';
  // A first start the China check stopped: never "Starting" forever (codex review f5).
  if (cloud?.lifecycle === 'active' && cloud.power === 'asleep' && CHINA_POWER_ERRORS.includes(cloud.powerError ?? '')) return 'stopped';
  return zone.status === 'connected' && cloud?.lifecycle === 'active' && cloud.power === 'awake' ? 'connected' : 'starting';
}

export type ChipTone = 'sealed' | 'neutral' | 'primary' | 'error';

/** Waking from its backup (freeze/thaw spec §3.8): a thaw or an availability-zone restore is under way. */
export function isRestoring(zone: Zone): boolean {
  const cloud = cloudOf(zone);
  return cloud !== null && cloud.power === 'waking' && cloud.powerReason === 'restoring';
}

/** The lifecycles a zone can be opened, woken and scheduled in: frozen too, which opens from its backup. */
export function isLiveCloud(cloud: CloudFacts | null): cloud is CloudFacts {
  return cloud !== null && (cloud.lifecycle === 'active' || cloud.lifecycle === 'dormant' || cloud.lifecycle === 'frozen');
}

/** The chip's word, as a catalogue key under `console:cloud.power` (the component translates it). */
export type PowerLabel = 'starting' | 'startFailed' | 'deleting' | 'deleted' | 'asleep' | 'awake' | 'goingToSleep' | 'restoring' | 'waking';

/** The chip is power, not connection (§3.2.3); lifecycles other than active and dormant keep their own words. */
export function powerChip(zone: Zone): { readonly label: PowerLabel; readonly tone: ChipTone; readonly pulsing: boolean } {
  const cloud = cloudOf(zone);
  const chip = (label: PowerLabel, tone: ChipTone, pulsing = false) => ({ label, tone, pulsing });
  if (cloud === null) return chip('starting', 'primary', true);
  switch (cloud.lifecycle) {
    case 'provisioning': return chip('starting', 'primary', true);
    case 'failed': return chip('startFailed', 'error');
    case 'deletion_pending': return chip('deleting', 'neutral');
    case 'deleted': return chip('deleted', 'neutral');
    // Asleep for a long time: it opens from its backup (freeze/thaw spec §3.8).
    case 'frozen': return chip('asleep', 'neutral');
    case 'dormant': return chip('asleep', 'neutral');
    default: break;
  }
  switch (cloud.power) {
    case 'awake': return chip('awake', 'sealed');
    case 'quiescing': case 'releasing': return chip('goingToSleep', 'neutral');
    case 'asleep': return chip('asleep', 'neutral');
    case 'waking': return chip(cloud.powerReason === 'restoring' ? 'restoring' : 'waking', 'primary', true);
    // Active before the first power state: still starting.
    default: return chip('starting', 'primary', true);
  }
}

/** Awake, for Open zone: anything else opens the wait page (§3.2.3). */
export function isAwake(zone: Zone): boolean {
  const cloud = cloudOf(zone);
  return cloud !== null && cloud.lifecycle === 'active' && cloud.power === 'awake';
}

export const CLOUD_REFRESH_MS = 15_000;
export const CLOUD_TRANSITION_REFRESH_MS = 3_000;
// Deletion pending is followed too, so the list sees the zone go (and its quota free) without a reload.
const LIVE: ReadonlySet<string> = new Set<CloudFacts['lifecycle']>(['provisioning', 'active', 'dormant', 'frozen', 'deletion_pending']);
const TRANSITIONS: ReadonlySet<string> = new Set<NonNullable<CloudFacts['power']>>(['waking', 'quiescing', 'releasing']);

/**
 * How often the list and the zone page refresh for the account's cloud zones (§3.2.3): every 3 s while a
 * power transition is under way, every 15 s while any is live, otherwise not at all (null).
 */
export function cloudRefreshMs(zones: readonly Zone[]): number | null {
  const clouds = zones.map(cloudOf).filter((cloud): cloud is CloudFacts => cloud !== null && LIVE.has(cloud.lifecycle));
  if (clouds.length === 0) return null;
  return clouds.some(cloud => cloud.power !== null && TRANSITIONS.has(cloud.power)) ? CLOUD_TRANSITION_REFRESH_MS : CLOUD_REFRESH_MS;
}

export const RECREATE_RETRY_MS = 10_000;
export const RECREATE_LIMIT_MS = 5 * 60_000;

/**
 * Try again's bounded recreation (§3.2.2): a create refused because the failed zone's address or quota is
 * not yet released is retried every 10 s for up to 5 minutes; anything else, or later, is shown in the form.
 */
export function recreateAction(code: string, elapsedMs: number): 'retry' | 'stop' {
  // `handle_taken` while the deleted tenant's teardown still holds its handle (readable intake addresses §4 case 15).
  return (code === 'tenant_name_taken' || code === 'handle_taken' || code === 'quota_reached') && elapsedMs + RECREATE_RETRY_MS <= RECREATE_LIMIT_MS ? 'retry' : 'stop';
}

export const WAIT_POLL_MS = 3_000;
const WAIT_POLL_MAX_MS = 30_000;

/**
 * The wait page's next state read (§3.2.4): every 3 s; on 429 never before the given Retry-After, and on
 * repeats doubling the previous wait, the doubling capped at 30 s.
 */
export function waitPollDelay(throttled: boolean, retryAfterSeconds: number | null, previousMs: number | null): number {
  if (!throttled) return WAIT_POLL_MS;
  const given = retryAfterSeconds !== null && Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0 ? retryAfterSeconds * 1_000 : WAIT_POLL_MS;
  return previousMs === null ? given : Math.max(given, Math.min(WAIT_POLL_MAX_MS, previousMs * 2));
}

/** ISO weekdays, as the pre-warm schedule stores them (control/src/cloud.ts). Their names come from the formatter. */
export const WEEKDAYS = [1, 2, 3, 4, 5, 6, 7] as const;

/** A day of the given ISO weekday, for naming it with a date formatter in UTC (1 January 2024 was a Monday). */
export function weekdayDate(day: number): Date {
  return new Date(Date.UTC(2024, 0, day));
}

/**
 * A region the catalogue names (`console:cloud.region.<key>`), for zones listed before the offer is read and so the
 * name follows the display language; null for a region it does not know, which keeps the offer's name or its id.
 */
const REGION_KEYS = { 'us-west-2': 'usWest2', 'ap-southeast-1': 'apSoutheast1' } as const;
export type RegionKey = typeof REGION_KEYS[keyof typeof REGION_KEYS];
export function regionKey(id: string): RegionKey | null {
  return Object.prototype.hasOwnProperty.call(REGION_KEYS, id) ? REGION_KEYS[id as keyof typeof REGION_KEYS] : null;
}

/** "Hong Kong" from "Asia/Hong_Kong": the schedule's zone as a place; null for UTC. */
export function timeZonePlace(timeZone: string): string | null {
  if (timeZone === 'UTC' || timeZone === 'Etc/UTC') return null;
  const parts = timeZone.split('/');
  return (parts[parts.length - 1] ?? timeZone).replace(/_/gu, ' ');
}

/** The time zone's own name in `language` ("香港时间"), or null when the runtime has none. */
export function timeZoneName(timeZone: string, language: string): string | null {
  try {
    // `longGeneric` is ES2022's: the lib this app targets does not name it.
    const options = { timeZone, timeZoneName: 'longGeneric' } as unknown as Intl.DateTimeFormatOptions;
    return new Intl.DateTimeFormat(language, options).formatToParts(0)
      .find(part => part.type === 'timeZoneName')?.value ?? null;
  } catch { return null; }
}
